import React, { useRef } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Reveal, WfButton, CreditChip } from "@/components/site/primitives";
import { ORG, TIMELINE } from "@/content/worldFarmers";
import { Link } from "react-router-dom";

export const AboutV2 = () => {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [reduce ? "0%" : "-6%", reduce ? "0%" : "6%"]);

  return (
    <section id="about" ref={ref} aria-labelledby="about-v2-heading" className="wf-section overflow-hidden bg-[var(--wf-paper)]">
      <div className="wf-container grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
        {/* Image with parallax */}
        <div className="lg:col-span-6">
          <div className="relative aspect-[4/5] overflow-hidden rounded-sm border border-[var(--wf-border)] shadow-[var(--wf-shadow)]">
            <motion.img
              style={reduce ? undefined : { y }}
              src="/images/wf/farm-3634.jpg"
              alt="A farmer with a sun hat kneeling to tend to crops in the field."
              loading="lazy"
              className="absolute inset-x-0 -top-[9%] h-[118%] w-full object-cover"
            />
            <CreditChip src={"/images/wf/farm-3634.jpg"} className="pointer-events-none absolute bottom-3 left-3 rounded-full bg-black/45 px-3 py-1 text-[11px] font-medium text-white/90 backdrop-blur-sm" />
          </div>
        </div>

        {/* Narrative */}
        <div className="lg:col-span-6">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-[var(--wf-maroon)]">
              Who we are · Since {ORG.founded}
            </span>
            <h2 id="about-v2-heading" className="mt-4 font-serif text-3xl font-semibold leading-tight text-[var(--wf-ink)] sm:text-4xl lg:text-5xl">
              Four decades of growing farmers, not just crops.
            </h2>
            <p className="mt-5 max-w-[58ch] text-base leading-7 text-muted-foreground">
              {ORG.missionShort} We provide mentoring, training, and hands-on assistance so every farmer can build a thriving enterprise — in a respectful environment of cross-cultural co-learning.
            </p>
          </Reveal>

          {/* Horizontal timeline */}
          <Reveal delay={0.1}>
            <ol className="mt-9 grid grid-cols-3 gap-4">
              {TIMELINE.map((t, i) => (
                <li key={t.year} className="relative">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-[var(--wf-maroon)]" aria-hidden="true" />
                    {i < TIMELINE.length - 1 && <span className="h-px flex-1 bg-[var(--wf-border)]" aria-hidden="true" />}
                  </div>
                  <div className="mt-3 font-serif text-2xl font-semibold text-[var(--wf-maroon)]">{t.year}</div>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">{t.text}</p>
                </li>
              ))}
            </ol>
          </Reveal>

          <Reveal delay={0.16}>
            <div className="mt-9">
              <WfButton as={Link} to="/about" withArrow data-testid="about-learn-more-button">
                Learn more about us
              </WfButton>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
